// ============================================================================
// CR071: MERCHANT INDEX - Client-side cache with stale-while-revalidate
// Single source of truth for merchant directory data
// ============================================================================

import { supabase } from "@/integrations/supabase/client";

// Types
export interface MerchantEntry {
  merchant_key: string;
  display_name: string;
  logo_url: string | null;
  logo_svg: string | null;
  logo_updated_at: string | null;
  category: string | null;
}

interface MerchantIndexCache {
  payload: MerchantEntry[];
  etag: string | null;
  cached_at: number;
  ttl_ms: number;
}

interface MerchantIndexResponse {
  version: string;
  generated_at: string;
  merchants: MerchantEntry[];
}

// Constants
const CACHE_KEY = "merchant_index_cache_v4"; // v4: force refresh for logo updates
const OLD_CACHE_KEYS = ["merchant_index_cache", "merchant_index_cache_v2", "merchant_index_cache_v3"]; // clean up old keys
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STALE_THRESHOLD_MS = 30 * 1000; // 30 seconds - revalidate quickly for fresher logos

// In-memory cache
let memoryCache: Map<string, MerchantEntry> | null = null;
let memoryCacheEtag: string | null = null;
let isRevalidating = false;

// Clean up old cache keys on module load (only in browser)
if (typeof window !== "undefined") {
  try {
    OLD_CACHE_KEYS.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
}

/**
 * Load cache from localStorage
 */
function loadLocalCache(): MerchantIndexCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MerchantIndexCache;
  } catch {
    return null;
  }
}

/**
 * Save cache to localStorage
 */
function saveLocalCache(cache: MerchantIndexCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn("[MerchantIndex] Failed to save cache:", e);
  }
}

/**
 * Build in-memory map from merchant array
 */
function buildMemoryCache(merchants: MerchantEntry[]): Map<string, MerchantEntry> {
  const map = new Map<string, MerchantEntry>();
  for (const m of merchants) {
    map.set(m.merchant_key, m);
  }
  return map;
}

/**
 * Fetch merchants from edge function with optional ETag
 */
async function fetchMerchantsFromApi(etag?: string | null): Promise<{
  merchants: MerchantEntry[] | null;
  newEtag: string | null;
  notModified: boolean;
}> {
  try {
    const headers: Record<string, string> = {};
    if (etag) {
      headers["If-None-Match"] = etag;
    }

    const { data, error } = await supabase.functions.invoke<MerchantIndexResponse>(
      "merchants-list",
      { headers }
    );

    // Check for 304 Not Modified (Supabase SDK may not expose status directly)
    // If data is null/empty and no error, likely 304
    if (!data && !error) {
      return { merchants: null, newEtag: etag, notModified: true };
    }

    if (error) {
      console.error("[MerchantIndex] API error:", error);
      return { merchants: null, newEtag: null, notModified: false };
    }

    // Extract ETag from response if available
    const newEtag = data?.generated_at ? `"${data.generated_at}"` : null;

    return {
      merchants: data?.merchants || [],
      newEtag,
      notModified: false,
    };
  } catch (e) {
    console.error("[MerchantIndex] Fetch error:", e);
    return { merchants: null, newEtag: null, notModified: false };
  }
}

/**
 * Background revalidation (stale-while-revalidate pattern)
 */
async function revalidateInBackground(): Promise<void> {
  if (isRevalidating) return;
  isRevalidating = true;

  try {
    const localCache = loadLocalCache();
    const { merchants, newEtag, notModified } = await fetchMerchantsFromApi(localCache?.etag);

    if (notModified) {
      // Update cached_at to prevent immediate revalidation
      if (localCache) {
        localCache.cached_at = Date.now();
        saveLocalCache(localCache);
      }
      return;
    }

    if (merchants) {
      // Update both caches
      memoryCache = buildMemoryCache(merchants);
      memoryCacheEtag = newEtag;

      saveLocalCache({
        payload: merchants,
        etag: newEtag,
        cached_at: Date.now(),
        ttl_ms: DEFAULT_TTL_MS,
      });
    }
  } finally {
    isRevalidating = false;
  }
}

/**
 * Initialize the merchant index
 * Call this early in app lifecycle (e.g., in App.tsx or first Deal Card render)
 */
export async function initMerchantIndex(): Promise<void> {
  // Check memory cache first
  if (memoryCache) return;

  // Try localStorage
  const localCache = loadLocalCache();
  const now = Date.now();

  if (localCache && localCache.payload.length > 0) {
    // Use cached data immediately
    memoryCache = buildMemoryCache(localCache.payload);
    memoryCacheEtag = localCache.etag;

    // Check if stale and needs revalidation
    const age = now - localCache.cached_at;
    if (age > STALE_THRESHOLD_MS) {
      // Fire background revalidation
      revalidateInBackground();
    }
    return;
  }

  // No cache - fetch fresh
  const { merchants, newEtag } = await fetchMerchantsFromApi();
  if (merchants) {
    memoryCache = buildMemoryCache(merchants);
    memoryCacheEtag = newEtag;

    saveLocalCache({
      payload: merchants,
      etag: newEtag,
      cached_at: now,
      ttl_ms: DEFAULT_TTL_MS,
    });
  }
}

/**
 * Get merchant by key from cache
 * Returns null if not found or cache not initialized
 */
export function getMerchant(merchantKey: string): MerchantEntry | null {
  if (!memoryCache) return null;
  return memoryCache.get(merchantKey) || null;
}

/**
 * Get all merchants from cache
 */
export function getAllMerchants(): MerchantEntry[] {
  if (!memoryCache) return [];
  return Array.from(memoryCache.values());
}

/**
 * Check if merchant index is loaded
 */
export function isMerchantIndexLoaded(): boolean {
  return memoryCache !== null && memoryCache.size > 0;
}

/**
 * Force refresh the merchant index
 */
export async function refreshMerchantIndex(): Promise<void> {
  memoryCache = null;
  memoryCacheEtag = null;
  localStorage.removeItem(CACHE_KEY);
  await initMerchantIndex();
}

/**
 * Get merchant display name (with fallback)
 */
export function getMerchantDisplayName(merchantKey: string): string {
  const merchant = getMerchant(merchantKey);
  if (merchant) return merchant.display_name;
  
  // Fallback: Convert key to title case
  return merchantKey
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
